import {z} from 'zod';
import {SignupSchema} from '../auth/types';
import {EntitySchema} from '../models/Entity';
import {SystemTheme, ThemeNames} from '../theme/themes';

const PreferencesSchema = z.object({
  locale: z.string().optional(),
  theme: z.enum([ThemeNames[0], ...ThemeNames.slice(1), SystemTheme]).optional(),
});

const AvatarSchema = z.object({
  source: z.enum(['auth', 'user']),
  url: z.url(),
});

export type UserAvatar = z.infer<typeof AvatarSchema>;
export type UserPreferences = z.infer<typeof PreferencesSchema>;

export const UserAuthSchema = z.object({
  membershipBindings: z.array(z.string()).optional(),
  memberships: z.record(z.string(), z.array(z.string())).optional(),
  permissions: z
    .object({
      global: z.record(z.string(), z.boolean()),
    })
    .optional(),
  roles: z.array(z.string()).optional(),
});

export type UserAuth = z.infer<typeof UserAuthSchema>;

export const UserSchema = EntitySchema.extend({
  __auth: UserAuthSchema.optional(),
  avatar: AvatarSchema.optional(),
  email: z.email(),
  id: z.string().min(1), // id = Firebase Auth UID, not the standard synthetic ID format used by other entities
  name: z.string(),
  preferences: PreferencesSchema,
  sessionLastExtendedTime: z.date().optional(),
});

export type User = z.infer<typeof UserSchema>;

export const UserPrototypeSchema = UserSchema.pick({
  avatar: true,
  email: true,
  name: true,
});

export type UserPrototype = z.infer<typeof UserPrototypeSchema>;

export const PublicProfileSchema = UserSchema.pick({
  avatar: true,
  id: true,
  name: true,
});

export type PublicProfile = z.infer<typeof PublicProfileSchema>;

export const UserSignupSchema = z.object({
  signup: SignupSchema,
  user: UserPrototypeSchema,
});

export type UserSignup = z.infer<typeof UserSignupSchema>;

export const RoleDefinitionSchema = z.object({
  description: z.string().optional(),
  id: z.string().min(1),
  isSystemRole: z.boolean().optional(),
  permissions: z.array(z.string()),
});

export type RoleDefinition = z.infer<typeof RoleDefinitionSchema>;

export const MembershipSchema = z.object({
  joinedAt: z.date(),
  role: z.string(),
  userId: z.string().min(1),
});

export type Membership = z.infer<typeof MembershipSchema>;
